import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import AxiosApiCall from "../api/AxiosApiCall";
import ApiServices from "../api/ApiServices";
import { toast } from "react-toastify";

const axiosClient = AxiosApiCall();

export const useRolesList = (options = {}) => {
  return useQuery({
    queryKey: ["rolesList"],
    queryFn: async () => {
      const response = await axiosClient.get(ApiServices.getRoles);
      return response.data?.data || [];
    },
    staleTime: 1000 * 60 * 10,
    ...options,
  });
};

export const useRoleById = (roleId, options = {}) => {
  return useQuery({
    queryKey: ["role", roleId],
    queryFn: async () => {
      const response = await axiosClient.get(ApiServices.getRolesPermission, {
        params: { role_id: roleId },
      });
      return response.data?.data || null;
    },
    enabled: !!roleId,
    staleTime: 1000 * 60 * 10,
    ...options,
  });
};

export const useCreateRole = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data) => {
      const response = await axiosClient.post(ApiServices.createRole, data);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["rolesList"] });
      toast.success(data?.message || "Role created successfully");
      if (options.onSuccess) options.onSuccess(data);
    },
    onError: (error) => {
      const message = error?.response?.data?.message || error?.message || "Failed to create role";
      toast.error(message);
      if (options.onError) options.onError(error);
    },
  });
};

export const useUpdateRole = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data) => {
      const response = await axiosClient.put(ApiServices.UpdateRole, data);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["rolesList"] });
      queryClient.invalidateQueries({ queryKey: ["role"] });
      toast.success(data?.message || "Role updated successfully");
      if (options.onSuccess) options.onSuccess(data);
    },
    onError: (error) => {
      const message = error?.response?.data?.message || error?.message || "Failed to update role";
      toast.error(message);
      if (options.onError) options.onError(error);
    },
  });
};

export const useAssignPermissionToRole = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data) => {
      const response = await axiosClient.post(ApiServices.assignPermissionToRole, data);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["rolesList"] });
      queryClient.invalidateQueries({ queryKey: ["role"] });
      toast.success(data?.message || "Permission assigned successfully");
      if (options.onSuccess) options.onSuccess(data);
    },
    onError: (error) => {
      const message = error?.response?.data?.message || error?.message || "Failed to assign permission";
      toast.error(message);
      if (options.onError) options.onError(error);
    },
  });
};

export const useDeleteRole = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (roleId) => {
      const response = await axiosClient.delete(ApiServices.deleteRoles, {
        params: { role_id: roleId },
      });
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["rolesList"] });
      toast.success(data?.message || "Role deleted successfully");
      if (options.onSuccess) options.onSuccess(data);
    },
    onError: (error) => {
      const message = error?.response?.data?.message || error?.message || "Failed to delete role";
      toast.error(message);
      if (options.onError) options.onError(error);
    },
  });
};
